import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Trash2, Sun } from "lucide-react";
import { toast } from "sonner";
import { fmtDate } from "@/lib/dates";
import { DatePicker } from "@/components/DatePicker";
export default function Holidays() {
  const { user } = useAuth();
  const [year, setYear] = useState(new Date().getFullYear());
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ date: "", name: "", description: "" });
  const isAdmin = user.role === "Admin";

  const load = async () => {
    const r = await api.get("/holidays", { params: { year } });
    setItems(r.data || []);
  };
  useEffect(() => { load(); }, [year]); // eslint-disable-line

  const add = async () => {
    try {
      await api.post("/holidays", form);
      toast.success("Added");
      setOpen(false); setForm({ date: "", name: "", description: "" });
      load();
    } catch { toast.error("Failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/holidays/${id}`);
    load();
  };

  const years = [year - 1, year, year + 1];

  return (
    <div className="min-h-screen bg-white" data-testid="holidays-page">
      <PageHeader
        title="Holidays"
        subtitle="Calendar Year"
        action={isAdmin && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#0a2540] hover:bg-[#0f2f50] rounded-sm" data-testid="btn-add-holiday">
                <Plus size={14} className="mr-1.5" /> Add Holiday
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm">
              <DialogHeader><DialogTitle style={{ fontFamily: "Outfit" }}>New Holiday</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Date *</Label>
                  <div className="mt-1"><DatePicker value={form.date} onChange={(v) => setForm({ ...form, date: v })} testId="input-holiday-date" /></div>
                </div>
                <div>
                  <Label className="text-xs">Name *</Label>
                  <Input className="mt-1 rounded-sm" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="input-holiday-name" />
                </div>
                <div>
                  <Label className="text-xs">Description</Label>
                  <Input className="mt-1 rounded-sm" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="input-holiday-desc" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancel</Button>
                <Button className="bg-[#0a2540] hover:bg-[#0f2f50] rounded-sm" onClick={add} data-testid="btn-save-holiday">Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      />
      <div className="px-4 sm:px-8 py-6">
        <div className="flex items-center gap-2 mb-5">
          {years.map((y) => (
            <button
              key={y}
              onClick={() => setYear(y)}
              data-testid={`year-${y}`}
              className={`px-3 py-1.5 text-xs font-mono-data border rounded-sm ${
                y === year ? "bg-[#0a2540] text-white border-[#0a2540]" : "bg-white text-slate-600 border-slate-200"
              }`}
            >
              {y}
            </button>
          ))}
        </div>
        <div className="border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-4 py-2.5">Date</th>
                <th className="text-left px-4 py-2.5">Day</th>
                <th className="text-left px-4 py-2.5">Holiday</th>
                <th className="text-left px-4 py-2.5">Description</th>
                <th className="px-4 py-2.5 w-12"></th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-xs text-slate-400">No holidays for {year}</td></tr>}
              {items.map((h) => (
                <tr key={h.id} className="border-b border-slate-100" data-testid={`holiday-row-${h.id}`}>
                  <td className="px-4 py-2.5 font-mono-data text-xs text-[#0a2540]">{fmtDate(h.date)}</td>
                  <td className="px-4 py-2.5 text-xs text-slate-600">{new Date(h.date).toLocaleDateString("en-IN", { weekday: "long" })}</td>
                  <td className="px-4 py-2.5 font-medium inline-flex items-center gap-1.5"><Sun size={12} className="text-amber-600" /> {h.name}</td>
                  <td className="px-4 py-2.5 text-xs text-slate-600">{h.description}</td>
                  <td className="px-4 py-2.5 text-right">
                    {isAdmin && (
                      <Button size="sm" variant="ghost" className="text-red-600 h-7 w-7 p-0" onClick={() => del(h.id)} data-testid={`btn-del-holiday-${h.id}`}>
                        <Trash2 size={12} />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
